import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Button,
  Grid,
  Divider,
  Pagination,
  Skeleton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
  Container,
  Stack,
  Paper,
} from '@mui/material';
import {
  AccessTime,
  LocationOn,
  MonetizationOn,
  Star,
  Close,
  Refresh,
  DirectionsCar,
} from '@mui/icons-material';
import { useSnackbar } from '../contexts/SnackbarContext';
import { rideAPI } from '../services/api';
import { format } from 'date-fns';

interface RideHistoryItem {
  id: string;
  status: 'completed' | 'cancelled';
  createdAt: string;
  origin: {
    address: string;
    latitude: number;
    longitude: number;
  };
  destination: {
    address: string;
    latitude: number;
    longitude: number;
  };
  driver?: {
    id: string;
    name: string;
    avatar?: string;
    rating: number;
    car: {
      make: string;
      model: string;
      color: string;
      plateNumber: string;
    };
  };
  fare: number;
  duration: number;
  distance: number;
  paymentMethod: string;
  rating?: number;
  review?: string;
}

const RideHistoryPage: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const [rides, setRides] = useState<RideHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRide, setSelectedRide] = useState<RideHistoryItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  useEffect(() => {
    fetchRideHistory();
  }, [page]);


  useEffect(() => {
    const handleRideCompleted = (event: any) => {
      console.log('🔄 Ride completed, refreshing history...', event.detail);

      setTimeout(() => {
        fetchRideHistory(1);
        setPage(1);
      }, 1000); 
    };

    window.addEventListener('rideCompleted', handleRideCompleted);


    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchRideHistory();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('rideCompleted', handleRideCompleted);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchRideHistory = async (forcePage?: number) => {
    try {
      setIsLoading(true);
      const currentPage = forcePage !== undefined ? forcePage : page;
      const response = await rideAPI.getRideHistory(currentPage, 10);

      console.log('📦 Ride history API response:', response);
      console.log('📦 Response type:', typeof response);
      console.log('📦 Response keys:', Object.keys(response || {}));

      let ridesData: any[] = [];
      let paginationData: any = null;


      if (response) {

        if (Array.isArray(response)) {
          ridesData = response;
        }

        else if (response.data) {
          if (Array.isArray(response.data)) {
            ridesData = response.data;
          } 

          else if (response.data.data && Array.isArray(response.data.data)) {
            ridesData = response.data.data;
            paginationData = response.data.pagination || response.data.meta;
          }

          else if (response.data.rides && Array.isArray(response.data.rides)) {
            ridesData = response.data.rides;
            paginationData = response.data.pagination || response.data.meta;
          }

          else if (response.data.items && Array.isArray(response.data.items)) {
            ridesData = response.data.items;
            paginationData = response.data.pagination || response.data.meta;
          }
        }

        else if (response.rides && Array.isArray(response.rides)) {
          ridesData = response.rides;
          paginationData = response.pagination || response.meta;
        }

        else if (response.items && Array.isArray(response.items)) {
          ridesData = response.items;
          paginationData = response.pagination || response.meta;
        }
      }

      console.log('✅ Extracted rides data:', ridesData);
      console.log('✅ Rides count:', ridesData.length);

      const transformedRides: RideHistoryItem[] = ridesData.map((ride: any) => {

        let calculatedDuration = ride.duration || ride.trip_duration || 0;
        if (!calculatedDuration && ride.startedAt && ride.completedAt) {
          const start = new Date(ride.startedAt).getTime();
          const end = new Date(ride.completedAt).getTime();
          calculatedDuration = Math.floor((end - start) / 1000); 
        }


        let calculatedDistance = ride.distance || ride.trip_distance || 0;
        if (!calculatedDistance) {
          const originLat = ride.originLatitude || ride.origin?.latitude || ride.locations?.pickup?.latitude || 0;
          const originLng = ride.originLongitude || ride.origin?.longitude || ride.locations?.pickup?.longitude || 0;
          const destLat = ride.destinationLatitude || ride.destination?.latitude || ride.locations?.dropoff?.latitude || 0;
          const destLng = ride.destinationLongitude || ride.destination?.longitude || ride.locations?.dropoff?.longitude || 0;

          if (originLat && originLng && destLat && destLng) {
            calculatedDistance = calculateDistanceInKm(originLat, originLng, destLat, destLng);
          }
        }


        let paymentMethod = ride.payment_method || ride.paymentMethod || ride.payment_method_name || '';
        if (!paymentMethod && ride.paymentMode) {
          paymentMethod = ride.paymentMode === 'online' ? 'Onlayn' : ride.paymentMode === 'offline' ? 'Nağd' : ride.paymentMode;
        }

        return {
          id: ride.id?.toString() || ride.ride_id?.toString() || '',
          status: ride.status === 'CANCELLED' || ride.status === 'cancelled' ? 'cancelled' : 'completed',
          createdAt: ride.created_at || ride.createdAt || ride.date || new Date().toISOString(),
          origin: {
            address: ride.originAddress || ride.start_point?.address || ride.origin?.address || ride.locations?.pickup?.address || ride.origin_address || '',
            latitude: ride.originLatitude || ride.start_point?.latitude || ride.origin?.latitude || ride.locations?.pickup?.latitude || 0,
            longitude: ride.originLongitude || ride.start_point?.longitude || ride.origin?.longitude || ride.locations?.pickup?.longitude || 0,
          },
          destination: {
            address: ride.destinationAddress || ride.end_point?.address || ride.destination?.address || ride.locations?.dropoff?.address || ride.destination_address || '',
            latitude: ride.destinationLatitude || ride.end_point?.latitude || ride.destination?.latitude || ride.locations?.dropoff?.latitude || 0,
            longitude: ride.destinationLongitude || ride.end_point?.longitude || ride.destination?.longitude || ride.locations?.dropoff?.longitude || 0,
          },
          driver: ride.driver ? {
            id: ride.driver.id?.toString() || ride.driver_id?.toString() || '',
            name: ride.driver.name || ride.driver.full_name || 'Sürücü',
            avatar: ride.driver.avatar || ride.driver.profile_picture,
            rating: ride.driver.rating || ride.driver.average_rating || 0,
            car: {
              make: ride.driver.vehicleInfo?.make || ride.driver.vehicle?.make || ride.driver.car?.make || 'Unknown',
              model: ride.driver.vehicleInfo?.model || ride.driver.vehicle?.model || ride.driver.car?.model || '',
              color: ride.driver.vehicleInfo?.color || ride.driver.vehicle?.color || ride.driver.car?.color || '',
              plateNumber: ride.driver.vehicleInfo?.plateNumber || ride.driver.vehicle?.plate_number || ride.driver.car?.plateNumber || '',
            },
          } : undefined,
          fare: ride.finalFare || ride.estimatedFare || ride.ride_fare || ride.fare || ride.final_fare || ride.estimated_fare || 0,
          duration: calculatedDuration,
          distance: calculatedDistance,
          paymentMethod: paymentMethod || 'Məlumat yoxdur',
          rating: ride.rating?.rating || ride.rating,
          review: ride.rating?.comment || ride.review || ride.comment,
        };
      });

      console.log('✅ Transformed rides:', transformedRides);

      setRides(transformedRides);

      if (paginationData) {
        setTotalPages(paginationData.totalPages || paginationData.total_pages || 1);
      } else {
        setTotalPages(transformedRides.length > 0 ? 1 : 1);
      }

    } catch (error: any) {
      console.error('❌ Failed to fetch ride history:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Sifariş tarixçəsi yüklənə bilmədi';
      showSnackbar(errorMessage, 'error');
      setRides([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleRefresh = () => {
    fetchRideHistory();
  };

  const handleRideDetails = (ride: RideHistoryItem) => {
    setSelectedRide(ride);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailModalOpen(false);
    setSelectedRide(null);
  };

  const getStatusText = (status: string) => {
    return status === 'completed' ? 'Tamamlandı' : 'Ləğv edildi';
  };

  const getStatusColor = (status: string) => {
    return status === 'completed' ? 'success' : 'error';
  };


  const calculateDistanceInKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; 
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd.MM.yyyy HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
      return `${hours} saat ${remainingMinutes} dəq`;
    }

    return `${minutes} dəq`;
  };

  const formatDistance = (meters: number) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} km`;
    }

    return `${meters} m`;
  };

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        py: { xs: 2, sm: 3 }, 
        px: { xs: 2, sm: 3 },
        minHeight: '100%',
        pb: { xs: 10, sm: 10 },
      }}
    >
      {}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #FF006E 0%, #2196F3 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: { xs: '1.75rem', sm: '2rem' },
            }}
          >
            Sifariş Tarixçəsi
          </Typography>
          <IconButton 
            onClick={handleRefresh} 
            color="primary" 
            disabled={isLoading}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              minWidth: { xs: 48, sm: 56 },
              minHeight: { xs: 48, sm: 56 },
              padding: { xs: 1.5, sm: 2 },
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              '&:hover': { bgcolor: 'primary.dark' },
              '&:active': {
                transform: 'scale(0.95)',
                transition: 'transform 0.1s ease-out',
              },
            }}
          >
            <Refresh />
          </IconButton>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          Bütün sifarişlərinizin siyahısı
        </Typography>
      </Box>

      {isLoading ? (
        <Stack spacing={2}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Card 
              key={index} 
              sx={{ 
                borderRadius: { xs: 3, sm: 4 },
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Skeleton variant="rectangular" height={24} width="40%" sx={{ mb: 2 }} />
                <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" height={20} sx={{ mb: 2 }} />
                <Box display="flex" justifyContent="space-between">
                  <Skeleton variant="text" width="30%" />
                  <Skeleton variant="text" width="30%" />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : rides.length > 0 ? (
        <>
          <Stack spacing={2}>
            {rides.map((ride) => (
              <Card 
                key={ride.id}
                sx={{
                  borderRadius: { xs: 3, sm: 4 },
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                    transition: 'transform 0.1s ease-out',
                  },
                }}
                onClick={() => handleRideDetails(ride)}
              >
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" alignItems="center">
                      <AccessTime sx={{ color: 'text.secondary', mr: 1, fontSize: { xs: 18, sm: 20 } }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        {formatDate(ride.createdAt)}
                      </Typography>
                    </Box>
                    <Chip
                      label={getStatusText(ride.status)}
                      color={getStatusColor(ride.status) as any}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  <Stack spacing={1.5} sx={{ mb: 2 }}>
                    <Paper
                      sx={{
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Box display="flex" alignItems="flex-start">
                        <LocationOn sx={{ color: 'primary.main', mr: 1.5, mt: 0.5, fontSize: { xs: 18, sm: 20 } }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                          {ride.origin.address}
                        </Typography>
                      </Box>
                    </Paper>
                    <Paper
                      sx={{
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Box display="flex" alignItems="flex-start">
                        <LocationOn sx={{ color: 'secondary.main', mr: 1.5, mt: 0.5, fontSize: { xs: 18, sm: 20 } }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                          {ride.destination.address}
                        </Typography>
                      </Box>
                    </Paper>
                  </Stack>

                  {ride.driver && (
                    <Box 
                      sx={{ 
                        mb: 2,
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <Avatar
                          src={ride.driver.avatar}
                          alt={ride.driver.name}
                          sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, mr: 1.5 }}
                        />
                        <Box flexGrow={1}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                            {ride.driver.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                            {ride.driver.car.make} {ride.driver.car.model} • {ride.driver.car.color}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Star sx={{ color: 'warning.main', fontSize: { xs: 16, sm: 18 }, mr: 0.5 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            {ride.driver.rating}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center">
                        <AccessTime sx={{ color: 'text.secondary', mr: 1, fontSize: { xs: 16, sm: 18 } }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {formatDuration(ride.duration)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center" justifyContent="flex-end">
                        <MonetizationOn sx={{ color: 'primary.main', mr: 1, fontSize: { xs: 18, sm: 20 } }} />
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                          ₼{ride.fare.toFixed(2)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {ride.rating && (
                    <Box display="flex" alignItems="center" mt={1.5}>
                      <Star sx={{ color: 'warning.main', fontSize: { xs: 16, sm: 18 }, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        {ride.rating}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
          )}
        </>
      ) : (
        <Card
          sx={{
            borderRadius: { xs: 3, sm: 4 },
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: { xs: 5, sm: 6 }, position: 'relative' }}>
            <Box
              sx={{
                width: { xs: 80, sm: 100 },
                height: { xs: 80, sm: 100 },
                borderRadius: '50%',
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <DirectionsCar sx={{ fontSize: { xs: 40, sm: 50 }, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
              Hələ sifarişiniz yoxdur
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              İlk sifarişinizi vermək üçün ana səhifəyə qayıdın
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.history.back()}
              size="large"
              sx={{
                minWidth: { xs: 120, sm: 140 },
                minHeight: { xs: 48, sm: 56 },
                padding: { xs: 1.5, sm: 2 },
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                '&:active': {
                  transform: 'scale(0.95)',
                  transition: 'transform 0.1s ease-out',
                },
              }}
            >
              Geri qayıt
            </Button>
          </CardContent>
        </Card>
      )}

      {}
      <Dialog
        open={isDetailModalOpen}
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 3, sm: 4 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Sifariş Detalları
          <IconButton
            aria-label="close"
            onClick={handleCloseDetails}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              minWidth: { xs: 48, sm: 56 },
              minHeight: { xs: 48, sm: 56 },
              padding: { xs: 1.5, sm: 2 },
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              '&:active': {
                transform: 'scale(0.95)',
                transition: 'transform 0.1s ease-out',
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedRide && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Sifariş vaxtı
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {formatDate(selectedRide.createdAt)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Sifariş statusu
                </Typography>
                <Box mt={1}>
                  <Chip
                    label={getStatusText(selectedRide.status)}
                    color={getStatusColor(selectedRide.status) as any}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                  Marşrut
                </Typography>
                <Stack spacing={1.5}>
                  <Paper sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Box display="flex" alignItems="flex-start">
                      <LocationOn sx={{ color: 'primary.main', mr: 1.5, mt: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedRide.origin.address}
                      </Typography>
                    </Box>
                  </Paper>
                  <Paper sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Box display="flex" alignItems="flex-start">
                      <LocationOn sx={{ color: 'secondary.main', mr: 1.5, mt: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedRide.destination.address}
                      </Typography>
                    </Box>
                  </Paper>
                </Stack>
              </Box>

              {selectedRide.driver && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                      Sürücü
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Avatar
                          src={selectedRide.driver.avatar}
                          alt={selectedRide.driver.name}
                          sx={{ width: 48, height: 48, mr: 2 }}
                        />
                        <Box flexGrow={1}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedRide.driver.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedRide.driver.car.make} {selectedRide.driver.car.model} • {selectedRide.driver.car.color}, {selectedRide.driver.car.plateNumber}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Star sx={{ color: 'warning.main', fontSize: 18, mr: 0.5 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {selectedRide.driver.rating}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                </>
              )}

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                    Müddət
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDuration(selectedRide.duration)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                    Məsafə
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDistance(selectedRide.distance)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                    Ödəniş üsulu
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedRide.paymentMethod || 'Məlumat yoxdur'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                    Qiymət
                  </Typography>
                  <Typography variant="body1" color="primary" sx={{ fontWeight: 700 }}>
                    ₼{selectedRide.fare.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>

              {selectedRide.rating && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                      Rəyiniz
                    </Typography>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Rating value={selectedRide.rating} readOnly size="small" />
                    </Box>
                    {selectedRide.review && (
                      <Paper sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="body2">
                          {selectedRide.review}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 1 }}>
          <Button 
            onClick={handleCloseDetails}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: { xs: 2, sm: 1.5 },
              minHeight: { xs: 56, sm: 48 },
              fontWeight: 600,
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              '&:active': {
                transform: 'scale(0.98)',
                transition: 'transform 0.1s ease-out',
              },
            }}
          >
            Bağla
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RideHistoryPage;
